import { webProgressRingClassName } from '@emovia/design-system';
import {
  getProgressRingGeometry,
  progressRingSizes,
  type ProgressRingSize,
} from '@emovia/design-system';
import { cn } from '@/lib/utils';

type CircularProgressProps = {
  value: number;
  label?: string;
  size?: ProgressRingSize;
  formatValue?: (value: number) => string;
  className?: string;
};

export function CircularProgress({
  value,
  label,
  size = 'md',
  formatValue = (val) => `${Math.round(val)}%`,
  className,
}: CircularProgressProps) {
  const sizeConfig = progressRingSizes[size];
  const { clamped, radius, circumference, dashoffset } = getProgressRingGeometry({
    value,
    size: sizeConfig.size,
    strokeWidth: sizeConfig.strokeWidth,
  });

  const resolved = webProgressRingClassName({ size });
  const slots =
    typeof resolved === 'string'
      ? { root: resolved, track: '', progress: '', value: '', label: '' }
      : resolved;

  return (
    <div className={cn('relative inline-flex', slots.root, className)}>
      <svg width={sizeConfig.size} height={sizeConfig.size} className="block">
        <circle
          className={slots.track}
          stroke="currentColor"
          fill="transparent"
          cx={sizeConfig.size / 2}
          cy={sizeConfig.size / 2}
          r={radius}
          strokeWidth={sizeConfig.strokeWidth}
        />
        <circle
          className={slots.progress}
          stroke="currentColor"
          fill="transparent"
          cx={sizeConfig.size / 2}
          cy={sizeConfig.size / 2}
          r={radius}
          strokeWidth={sizeConfig.strokeWidth}
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={dashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${sizeConfig.size / 2} ${sizeConfig.size / 2})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <strong className={slots.value}>{formatValue(clamped)}</strong>
        {label ? <small className={slots.label}>{label}</small> : null}
      </div>
    </div>
  );
}
